import React from "react";

import { checkAdminAccess, goTo, useAdminUserContext, useLocation, useNavContext, useNavigate } from "utils";

import { PRACTICE_MANAGEMENT_TABS } from "consts";

import { Divider, Icon, Layout, Menu, MenuProps, Tooltip } from "components";

type AdminMenuItems = NonNullable<MenuProps["items"]>;
type MenuClickInfo = Parameters<NonNullable<MenuProps["onClick"]>>[0];

export const AdminNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showNavbar } = useNavContext();
  const { user, signOut } = useAdminUserContext();

  const submenuItems = [
    {
      submenu: "content",
      items: [
        {
          name: "Articles",
          route: "content/articles",
        },
        {
          name: "News",
          route: "content/news",
        },
        {
          name: "Popups",
          route: "content/popups",
        },
      ],
    },
    {
      submenu: "farms",
      items: [
        {
          name: "Farms",
          route: "farms/farms",
        },
        {
          name: "Fields",
          route: "farms/fields",
        },
        {
          name: "Shapefiles",
          route: "farms/shapefiles",
        },
      ],
    },
    {
      submenu: "carbonPrograms",
      items: [
        {
          name: "Carbon Programs",
          route: "carbon-programs/overview",
        },
        {
          name: "Carbon Program Participation",
          route: "carbon-programs/participation",
        },
        {
          name: "Carbon Program Dates",
          route: "carbon-programs/dates",
        },
        {
          name: "Program Drafts",
          route: "carbon-programs/drafts",
        },
      ],
    },
    {
      submenu: "practiceManagement",
      items: PRACTICE_MANAGEMENT_TABS.map(({ key, label }) => ({
        name: label,
        route: `practice-management/${key}`,
      })),
    },
    {
      submenu: "payments",
      items: [
        {
          name: "Pending payments",
          route: "payments/pending",
        },
        {
          name: "Past payments",
          route: "payments/past",
        },
        {
          name: "Referral payments",
          route: "payments/referral",
        },
        {
          name: "Vesting",
          route: "payments/vesting",
        },
      ],
    },
    {
      submenu: "partners",
      items: [
        {
          name: "Partners",
          route: "partners/partners",
        },
        {
          name: "Predictions",
          route: "partners/predictions",
        },
      ],
    },
    {
      submenu: "programs",
      items: [
        {
          name: "EU funding",
          route: "programs/funding",
        },
        {
          name: "Digital Agronomist Crop Practices",
          route: "programs/digital-agronomist-crop-practices",
        },
        {
          name: "Crop Mix Composer Commercial Mixes",
          route: "programs/crop-mix-composer-commercial-mixes",
        },
      ],
    },
    {
      submenu: "redzones",
      items: [
        {
          name: "Exceptions",
          route: "redzones/exceptions",
        },
        {
          name: "Shapefile upload",
          route: "redzones/shapefile-uploads",
        },
      ],
    },
    {
      submenu: "users",
      items: [
        {
          name: "Email tracking",
          route: "users/tracking",
        },
        {
          name: "Users",
          route: "users/users",
        },
        {
          name: "User notifications",
          route: "users/notifications",
        },
      ],
    },
    {
      submenu: "verifications",
      items: [
        {
          name: "Farms",
          route: "verifications/farms",
          dataTestId: "admin-nav-farms",
        },
        {
          name: "Tasks",
          route: "verifications/tasks",
          dataTestId: "admin-nav-tasks",
        },
        {
          name: "Verification Status Upload",
          route: "verifications/verification-status",
          dataTestId: "admin-nav-verification-status",
        },
        {
          name: "Quick field access",
          route: "verifications/quick-field-access",
          dataTestId: "admin-nav-quick-field-access",
        },
        {
          name: "NDVI Scores",
          route: "verifications/ndvi-scores",
          dataTestId: "admin-nav-ndvi-scores",
        },
        {
          name: "Doc-Scan",
          route: "verifications/doc-scans",
          dataTestId: "admin-nav-doc-scans",
        },
        {
          name: "Field change events",
          route: "verifications/field-change-events",
          dataTestId: "admin-nav-field-change-events",
        },
        {
          name: "Certifications",
          route: "verifications/certifications",
          dataTestId: "admin-nav-certifications",
        },
      ],
    },
    {
      submenu: "experiments",
      items: [
        {
          name: "General Experiments",
          route: "soilplus",
        },
        {
          name: "SoilAI Experiments",
          route: "soilai",
        },
        {
          name: "Soil Sight Single Version",
          route: "soil-sight-single-version",
        },
        {
          name: "Soil Sight Multiple Version",
          route: "soil-sight-multiple-version",
        },
      ],
    },
    {
      submenu: "admin",
      items: [
        {
          name: "Admin settings",
          route: "settings/settings",
        },
        {
          name: "Verification recalculation",
          route: "settings/verification-recalculation",
        },
        {
          name: "Emission Factors",
          route: "settings/emission-factors",
        },
        {
          name: "Admin users",
          route: "settings/users",
        },
        {
          name: "Platform Terms",
          route: "settings/platform-terms",
        },
        {
          name: "Feature Flags",
          route: "settings/feature-flags",
        },
        {
          name: "FeatBit Segments",
          route: "settings/featbit-segments",
        },
      ],
    },
  ];

  const getMenuItems = (submenu: string) => {
    const menu: AdminMenuItems = [];
    submenuItems.forEach((item) => {
      if (item.submenu === submenu) {
        item.items.forEach((list) => {
          if (user && checkAdminAccess(user.role, list.route)) {
            menu.push({
              label: list.name,
              key: `${list.route}`,
              onClick: (e: MenuClickInfo) => goTo(e, navigate, `/${list.route}`),
            });
          } else {
            menu.push({
              label: list.name,
              key: `${list.route}`,
              disabled: true,
            });
          }
        });
      }
    });

    return menu;
  };

  const menuItems: AdminMenuItems = [
    {
      icon: <Icon iconName="tabler/home" />,
      onClick: (e: MenuClickInfo) => goTo(e, navigate, "/dashboard"),
      key: "dashboard",
    },
    {
      icon: <Icon iconName="tabler/file-text" />,
      key: "content",
      children: getMenuItems("content"),
    },
    {
      icon: <Icon iconName="tabler/map-2" />,
      key: "farms",
      children: getMenuItems("farms"),
    },
    {
      icon: <Icon iconName="tabler/leaf" />,
      key: "carbonPrograms",
      children: getMenuItems("carbonPrograms"),
    },
    {
      icon: <Icon iconName="tabler/building-factory" />,
      key: "practiceManagement",
      children: getMenuItems("practiceManagement"),
    },
    {
      icon: <Icon iconName="tabler/coin" />,
      key: "payments",
      children: getMenuItems("payments"),
    },
    {
      icon: <Icon iconName="tabler/affiliate" />,
      key: "partners",
      children: getMenuItems("partners"),
    },
    {
      icon: <Icon iconName="tabler/layers-subtract" />,
      key: "programs",
      children: getMenuItems("programs"),
    },
    {
      icon: <Icon iconName="klim/bottle-strikethrough" />,
      key: "redzones",
      children: getMenuItems("redzones"),
    },
    {
      icon: <Icon iconName="tabler/users" />,
      key: "users",
      children: getMenuItems("users"),
    },
    {
      icon: <Icon dataTestId="admin-nav-verifications" iconName="tabler/eye-check" />,
      key: "verifications",
      children: getMenuItems("verifications"),
    },
    {
      icon: <Divider className="margin-0 margin-top-100 margin-bottom-100" />,
      key: "divider",
    },
    {
      icon: <Icon iconName="tabler/flask" />,
      key: "experiments",
      children: getMenuItems("experiments"),
    },
    {
      icon: <Icon iconName="tabler/settings" />,
      key: "admin",
      children: getMenuItems("admin"),
    },
    {
      icon: (
        <Tooltip title="Sign out" placement="right">
          <Icon iconName="tabler/logout" dataTestId="admin-nav-sign-out" />
        </Tooltip>
      ),
      key: "sign-out",
      onClick: signOut,
    },
  ];

  const { Sider } = Layout;

  return showNavbar ? (
    <Sider className="admin-sidebar">
      <Menu mode="vertical" selectedKeys={[location.pathname.split("/")[1]]} items={menuItems} />
    </Sider>
  ) : null;
};

export default AdminNav;
